import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const DeleteAccount = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user || user.role === "admin") return null;

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/account", { data: { password } });
      await logout();
      toast.success("Your account and data have been deleted.");
      nav("/");
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="panel p-5 border-rose-500/30 flex flex-wrap items-center justify-between gap-4" data-testid="delete-account-panel">
      <div>
        <div className="text-white font-semibold">Delete my account</div>
        <p className="text-xs text-slate-400 mt-1 max-w-xl">Permanently removes your account, saved races and settings. This can't be undone. Passes already bought are not refunded.</p>
      </div>
      <Button variant="outline" className="border-rose-500/50 text-rose-300 hover:bg-rose-500/10" onClick={() => setOpen(true)} data-testid="delete-account-open">
        <Trash2 className="h-4 w-4 mr-2" />Delete my account
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPassword(""); setWord(""); } }}>
        <DialogContent className="bg-[#0F172A] border-slate-700" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>Enter your password and type DELETE to permanently remove {user.email} and all its data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password"
              className="bg-[#080C14] border-slate-700 text-white" data-testid="delete-account-password" />
            <input value={word} onChange={(e) => setWord(e.target.value)} placeholder="Type DELETE"
              className="w-full bg-[#080C14] border border-slate-700 rounded px-3 py-2 text-sm text-white font-mono" data-testid="delete-account-confirm-word" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="delete-account-cancel">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-500 text-white" disabled={busy || !password || word !== "DELETE"} onClick={remove} data-testid="delete-account-confirm">
              {busy ? "Deleting..." : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
